import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '@/theme/tokens';
import { Inline, Stack, StatusBanner } from '@/ui';
import { Box, Button, Link, MenuItem, Text, TextField } from '@/ui/primitives';
import type { FormEvent, ReactElement } from 'react';

type EarlyAccessSectionProps = {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  role: string;
  companySize: string;
  onFirstNameChange: (next: string) => void;
  onLastNameChange: (next: string) => void;
  onCompanyNameChange: (next: string) => void;
  onEmailChange: (next: string) => void;
  onRoleChange: (next: string) => void;
  onCompanySizeChange: (next: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  width: number;
  isSubmitting: boolean;
  submitStatus: 'idle' | 'success' | 'error';
};

export const EarlyAccessSection = ({
  firstName,
  lastName,
  companyName,
  email,
  role,
  companySize,
  onFirstNameChange,
  onLastNameChange,
  onCompanyNameChange,
  onEmailChange,
  onRoleChange,
  onCompanySizeChange,
  onSubmit,
  width,
  isSubmitting,
  submitStatus,
}: EarlyAccessSectionProps): ReactElement => (
  <Box
    component="section"
    id="request-early-access"
    sx={(theme) => ({
      position: 'relative',
      left: '50%',
      right: '50%',
      width: '100vw',
      ml: '-50vw',
      mr: '-50vw',
      px: s('none'),
      py: s('2xl'),
      background: `linear-gradient(120deg, ${theme.app.colors.surfaceAlt} 0%, ${theme.app.colors.surface} 46%, ${theme.app.colors.primary}12 70%, ${theme.app.colors.surfaceAlt} 85%, ${theme.app.colors.surface} 100%)`,
    })}
  >
    <Box
      sx={{
        maxWidth: width,
        mx: 'auto',
        pt: s('2xl'),
        pb: s('3xl'),
        px: s('lg'),
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
          gap: s('3xl'),
          alignItems: 'start',
        }}
      >
        <Stack gap="md">
          <Text role="sectionTitle" component="h2" transform="none">
            Register Interest
          </Text>
          <Text role="bodyMuted" component="p">
            Tell us a bit about yourself and we’ll follow up with early access details.
          </Text>
          <Text role="bodyMuted" component="p">
            We’re working with a small group of organisations focused on improving quality, to
            understand their challenges and shape FlowQA around real needs.
          </Text>
          <Text role="bodyMuted" component="p">
            If that sounds like a good fit, register your interest or reach out directly at{' '}
            <Link href="mailto:chris@flowqa.com" underline="none">
              chris@flowqa.com
            </Link>
            .
          </Text>
        </Stack>
        <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
          <Stack gap="lg" sx={{ width: '100%' }}>
            <Inline gap="md" wrap="wrap" sx={{ width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  value={firstName}
                  onChange={(event) => {
                    onFirstNameChange(event.target.value);
                  }}
                  placeholder="First Name"
                  inputHeight={componentSizes.auth.inputHeight}
                  required
                  slotProps={{ htmlInput: { 'aria-label': 'First name' } }}
                  sx={{ width: '100%' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  value={lastName}
                  onChange={(event) => {
                    onLastNameChange(event.target.value);
                  }}
                  placeholder="Last Name"
                  inputHeight={componentSizes.auth.inputHeight}
                  required
                  slotProps={{ htmlInput: { 'aria-label': 'Last name' } }}
                  sx={{ width: '100%' }}
                />
              </Box>
            </Inline>
            <Box>
              <TextField
                value={email}
                onChange={(event) => {
                  onEmailChange(event.target.value);
                }}
                type="email"
                placeholder="Email"
                inputHeight={componentSizes.auth.inputHeight}
                required
                slotProps={{ htmlInput: { 'aria-label': 'Email' } }}
                sx={{ width: '100%' }}
              />
            </Box>
            <Box>
              <TextField
                value={companyName}
                onChange={(event) => {
                  onCompanyNameChange(event.target.value);
                }}
                placeholder="Company"
                inputHeight={componentSizes.auth.inputHeight}
                slotProps={{ htmlInput: { 'aria-label': 'Company' } }}
                sx={{ width: '100%' }}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: s('md'),
                width: '100%',
              }}
            >
              <TextField
                value={role}
                onChange={(event) => {
                  onRoleChange(event.target.value);
                }}
                placeholder="Role"
                inputHeight={componentSizes.auth.inputHeight}
                slotProps={{ htmlInput: { 'aria-label': 'Role' } }}
                sx={{ width: '100%' }}
              />
              <TextField
                select
                value={companySize}
                onChange={(event) => {
                  onCompanySizeChange(event.target.value as string);
                }}
                slotProps={{
                  htmlInput: { 'aria-label': 'Company size' },
                  select: { displayEmpty: true },
                }}
                placeholder="Company Size"
                inputHeight={componentSizes.auth.inputHeight}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: r('md'),
                    minHeight: `${componentSizes.auth.inputHeight}px !important`,
                    height: `${componentSizes.auth.inputHeight}px !important`,
                  },
                  '& .MuiSelect-select': { backgroundColor: 'background.paper' },
                  '& .MuiOutlinedInput-notchedOutline': { borderRadius: r('md') },
                }}
              >
                <MenuItem value="">
                  <Text role="bodyMuted" component="span">
                    Company Size
                  </Text>
                </MenuItem>
                <MenuItem value="1-10">1-10</MenuItem>
                <MenuItem value="11-50">11-50</MenuItem>
                <MenuItem value="51-200">51-200</MenuItem>
                <MenuItem value="200+">200+</MenuItem>
              </TextField>
            </Box>
            <Inline justify="stretch" sx={{ mt: s('none'), width: '100%' }}>
              <Button
                type="submit"
                variant="primary"
                fullWidthOnMobile
                disabled={isSubmitting}
                sx={{ width: { xs: '100%', sm: '100%' }, justifyContent: 'center' }}
              >
                {isSubmitting ? 'Submitting...' : 'Register Interest'}
              </Button>
            </Inline>
            {submitStatus === 'success' ? (
              <StatusBanner tone="green" dense message="Thanks! We'll be in touch soon." />
            ) : null}
            {submitStatus === 'error' ? (
              <StatusBanner tone="red" dense message="Something went wrong. Please try again." />
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Box>
  </Box>
);
